import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FlexContainer } from '@taxdown/piccolo-web';
import { setTaxSelectedAction } from 'Store/modules/tax/actions';
import { setStageAction } from 'Store/modules/validations/actions';
import { taxSelectedSelector } from 'Store/modules/tax/selectors';
import { useFlags } from 'Hooks/useFlags';
import { useGetPlanFlexInfo } from 'Containers/Result/hooks';
import { useGetUserSatInfo } from 'Containers/PreviousYears/hooks';
import { useFetchReintents } from 'Containers/Datagathering/views/Loader/hooks';
import ReferralBanner from 'Components/ReferralBanner';
import { DomiciliationStatus } from 'Constants/stages';
import { FlagsKey } from 'Types/common';
import TaxesList from './components/TaxesList';
import TaxScreen from './components/TaxScreen';
import * as Styled from './styles';
import {
  useFetchPreviousYear,
  useFetchSeasons,
  useGetFlexInvoices,
  useGetUserChargements,
} from './hooks';
import UpgradeToProBanner from './components/UpgradeToProBanner';
import DomiciliationBanner from './components/TaxScreen/components/DomiciliationBanner';

const Dashboard = (): JSX.Element => {
  const dispatch = useDispatch();
  const taxInfo = useSelector(taxSelectedSelector);
  const { isLoading, data: taxes } = useFetchSeasons();
  const { isLoading: loadingFlexInvoice } = useGetFlexInvoices();
  const { isLoading: loadingFlexInfo } = useGetPlanFlexInfo();
  const { isLoading: loadingPreviousYear } = useFetchPreviousYear();
  const { isLoading: isLoadingSatUserData } = useGetUserSatInfo();
  const { isLoading: isLoadingReintent } = useFetchReintents();
  const { isLoading: isLoadingChargements, data: userChargements } = useGetUserChargements(
    taxInfo?.recordId || ''
  );
  const [activeTax, setActiveTax] = useState(taxes?.[0]);
  const { enabled } = useFlags(FlagsKey.MGM_FLOW_ENABLED);
  const handleOnActiveTax = (recordId: string, taxYear: string | number): void => {
    const tax = taxes?.find(item => item.taxYear.toString() === taxYear.toString());
    if (tax) {
      const domiciliationStatus = userChargements?.data?.item?.actionStatus;
      setActiveTax({
        ...tax,
        stage:
          domiciliationStatus === DomiciliationStatus.AUTOMATIC_PAYMENT_FAILED
            ? domiciliationStatus
            : tax.stage,
      });
      dispatch(
        setStageAction(
          domiciliationStatus === DomiciliationStatus.AUTOMATIC_PAYMENT_FAILED
            ? domiciliationStatus
            : tax.stage
        )
      );
      dispatch(setTaxSelectedAction({ recordId, taxYear }));
    }
  };

  useEffect(() => {
    if (taxes) {
      const { taxYear, recordId } = taxes[0] || {};
      handleOnActiveTax(recordId, taxYear);
    }
  }, [taxes]);

  const isLoadingUserChargements = taxInfo?.recordId ? isLoadingChargements : false;

  const loadingState =
    isLoading ||
    loadingFlexInvoice ||
    loadingFlexInfo ||
    loadingPreviousYear ||
    isLoadingSatUserData ||
    isLoadingReintent ||
    isLoadingUserChargements;

  return (
    <Styled.ThreeColLayout>
      <TaxesList
        taxes={taxes}
        isLoading={isLoading}
        activeTax={activeTax?.taxYear}
        onClick={handleOnActiveTax}
      />
      <TaxScreen tax={activeTax} isLoading={loadingState} />
      <FlexContainer direction="column">
        <Styled.BannersContainer gap="dimension_16" direction="column">
          {!enabled && <ReferralBanner />}
          <UpgradeToProBanner />
          {activeTax && <DomiciliationBanner tax={activeTax} />}
        </Styled.BannersContainer>
      </FlexContainer>
    </Styled.ThreeColLayout>
  );
};

export default Dashboard;
